import { DialogProvider, SelectProvider } from '@ariakit/react'
import { useState } from 'react'
import { GalleryImageItem } from '@/components/commons/gallery-image-item'
import { DialogDisclosure } from '@/components/ui/dialog-atoms'
import { Image03Icon } from '@/components/ui/icons'
import {
	SelectItem,
	SelectList,
	SelectPopover,
	SelectTrigger
} from '@/components/ui/select-atoms-legacy'
import { SelectItineraryPhotoDialogBody } from '@/features/experience-shell/components/select-itinerary-photo-dialog-body'
import { useEditExperienceItinerary } from '@/features/experience-shell/queries/use-edit-experience-itinerary.ts'
import type { Itinerary } from '@/features/experience-shell/types.ts'
import i18n, { useTranslation } from '@/translations/i18n'

const getDurationOptions = () => {
	const options: { label: string; value: string }[] = []
	for (let minutes = 15; minutes <= 120; minutes += 15) {
		options.push({
			label: i18n.t('experience_shell:x_minutes', { count: minutes }),
			value: minutes.toString()
		})
	}
	return options
}
const durationOptions = getDurationOptions()

export const ActivityCard = ({
	experienceId,
	galleryId,
	itinerary
}: {
	experienceId: number
	galleryId: number
	itinerary: Itinerary
}) => {
	const { t } = useTranslation()

	const [isOpen, setIsOpen] = useState(false)
	const editItinerary = useEditExperienceItinerary({
		urlParams: { itineraryId: itinerary.id, experienceId }
	})

	const onConfirmSelectPhoto = (id: number) => {
		editItinerary.mutate({ coverImageId: id })
	}
	const onChangeDuration = (durationInMinutes: string) => {
		editItinerary.mutate({
			durationInMinutes: Number.parseInt(durationInMinutes, 10)
		})
	}

	return (
		<div className='flex w-full gap-5 text-neutral-900 [&_button]:w-[240px]'>
			<DialogProvider setOpen={() => setIsOpen(true)}>
				<DialogDisclosure
					disabled={
						editItinerary.isPending &&
						!!editItinerary.variables.durationInMinutes
					}
					render={
						itinerary.coverImage ? (
							<GalleryImageItem
								className='h-[108px] w-[108px] rounded-2xl'
								src={
									itinerary.coverImage.thumbnails?.xs ||
									itinerary.coverImage.url
								}
							/>
						) : (
							<div className='flex h-[108px] w-[108px] shrink-0 cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-neutral-300'>
								<Image03Icon className='h-6 w-6 fill-neutral-600' />
								<p className='font-medium text-neutral-900 text-xs'>
									{t('common:choose_a_photo')}
								</p>
							</div>
						)
					}
				/>
				{isOpen && (
					<SelectItineraryPhotoDialogBody
						defaultSelectedImageId={itinerary.coverImageId}
						experienceId={experienceId}
						galleryId={galleryId}
						onClose={() => setIsOpen(false)}
						onConfirm={onConfirmSelectPhoto}
					/>
				)}
			</DialogProvider>
			<div className='flex flex-col gap-4'>
				<div className='flex flex-col gap-2'>
					<h3 className='font-medium'>{itinerary.title}</h3>
					<p className='text-neutral-700 text-sm'>
						{itinerary.description}
					</p>
				</div>
				<SelectProvider
					setValue={onChangeDuration}
					value={
						itinerary.durationInMinutes
							? itinerary.durationInMinutes.toString()
							: ''
					}
				>
					<SelectTrigger
						disabled={
							editItinerary.isPending &&
							!!editItinerary.variables.durationInMinutes
						}
						displayValue={(value) =>
							durationOptions.find(
								(option) => option.value === value
							)?.label
						}
						placeholder={t('experience_shell:select_duration')}
					/>
					<SelectPopover>
						<SelectList>
							{durationOptions.map((option) => (
								<SelectItem
									key={option.value}
									value={option.value}
								>
									{option.label}
								</SelectItem>
							))}
						</SelectList>
					</SelectPopover>
				</SelectProvider>
			</div>
		</div>
	)
}
